import * as THREE from 'three';
import { TextureLoader } from 'three';

interface BillboardData {
  id: string;
  title: string;
  description: string;
  url: string;
  imageUrl: string;
}

export class BillboardManager {
  private scene: THREE.Scene;
  private camera: THREE.Camera;
  private raycaster: THREE.Raycaster;
  private mouse: THREE.Vector2;
  private billboards: Map<string, THREE.Mesh> = new Map();
  private billboardData: Map<string, BillboardData> = new Map();
  private clickEventListener: ((event: MouseEvent) => void) | null = null;
  private overlayContainer: HTMLElement | null = null;
  
  constructor(scene: THREE.Scene, camera: THREE.Camera) {
    this.scene = scene;
    this.camera = camera;
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    
    // Create overlay container
    this.createOverlayContainer();
    
    // Setup click event listener
    this.setupClickListener();
  }
  
  /**
   * Creates the overlay container for billboard popups
   */
  private createOverlayContainer(): void {
    // Check if overlay already exists
    let container = document.getElementById('billboard-overlay-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'billboard-overlay-container';
      document.body.appendChild(container);
    }
    
    this.overlayContainer = container;
  }
  
  /**
   * Setup click event listener for billboard interaction
   */
  private setupClickListener(): void {
    this.clickEventListener = (event: MouseEvent) => {
      // Calculate normalized device coordinates
      this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
      
      // Update the raycaster
      this.raycaster.setFromCamera(this.mouse, this.camera);
      
      // Get all objects in the scene that could be billboards
      const billboardObjects: THREE.Object3D[] = [];
      this.scene.traverse((object) => {
        if (object.userData && (object.userData.isBillboard || object.userData.billboardId)) {
          billboardObjects.push(object);
        }
      });
      
      // Check for intersections with a larger threshold
      const intersects = this.raycaster.intersectObjects(billboardObjects, true);
      
      if (intersects.length > 0) {
        // Find the closest intersected billboard
        const intersectedObject = intersects[0].object;
        
        // Get the billboard id either from the object or by finding a parent with billboard data
        let billboardId = intersectedObject.userData.billboardId;
        
        // If the object doesn't have a billboard id, search up the parent chain
        if (!billboardId) {
          let current = intersectedObject;
          while (current.parent && !current.userData.billboardId) {
            current = current.parent;
          }
          billboardId = current.userData.billboardId;
        }
        
        // Show the overlay if we found a valid billboard id
        if (billboardId) {
          console.log("Billboard clicked:", billboardId);
          this.showOverlay(billboardId);
        }
      }
    };
    
    // Add click event listener
    window.addEventListener('click', this.clickEventListener);
  }
  
  /**
   * Create a billboard at the specified position
   */
  public createBillboard(
    id: string,
    position: THREE.Vector3,
    rotation: number = 0,
    width: number = 10,
    height: number = 5,
    data: BillboardData
  ): void {
    // Create data for both sides (using the same data)
    this.createBillboardWithBackSide(
      data,
      data,
      position,
      rotation,
      width,
      height
    );
  }
  
  /**
   * Create one side of a billboard (front or back)
   */
  private createBillboardSide(
    parent: THREE.Group,
    boardMaterial: THREE.Material,
    width: number,
    height: number,
    postHeight: number,
    zOffset: number,
    data: BillboardData,
    isFront: boolean
  ): void {
    // Create the main billboard board
    const boardGeometry = new THREE.BoxGeometry(width, height, 0.05);
    const board = new THREE.Mesh(boardGeometry, boardMaterial);
    
    // Position the board
    board.position.set(0, postHeight - height/2 - 1, zOffset);
    
    // Rotate the back side to face the opposite direction
    if (!isFront) {
      board.rotation.y = Math.PI;
    }
    
    // Set user data for raycasting
    board.userData.isBillboard = true;
    board.userData.billboardId = data.id;
    board.castShadow = true;
    board.receiveShadow = true;
    parent.add(board);
    
    // Load and apply image texture if provided
    if (data.imageUrl) {
      this.loadAndDisplayImage(board, data, width, height);
    } else {
      // Fallback - just render the title text centered if no image
      this.renderTextOnBillboard(board, data.title, width, height, true, data.id);
    }
  }
  
  /**
   * Load and display the image on the billboard
   */
  private loadAndDisplayImage(
    parent: THREE.Object3D,
    data: BillboardData,
    boardWidth: number,
    boardHeight: number
  ): void {
    const textureLoader = new TextureLoader();
    textureLoader.load(
      data.imageUrl,
      (texture) => {
        // Improve texture quality
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.minFilter = THREE.LinearMipMapLinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.anisotropy = 16; // Higher anisotropy for sharper textures
        
        // Create a material with the loaded texture
        const imageMaterial = new THREE.MeshBasicMaterial({
          map: texture,
          side: THREE.FrontSide,
          transparent: true
        });
        
        // Set fixed image dimensions - 90% of billboard size
        const imageWidth = boardWidth * 0.9;
        const imageHeight = boardHeight * 0.9;
        
        // Create plane geometry for the image
        const imageGeometry = new THREE.PlaneGeometry(imageWidth, imageHeight);
        const imagePlane = new THREE.Mesh(imageGeometry, imageMaterial);
        
        // Make the image plane clickable
        imagePlane.userData.isBillboard = true;
        imagePlane.userData.billboardId = data.id;
        
        // Position the image centered on the billboard
        imagePlane.position.set(0, 0, 0.03);
        parent.add(imagePlane);
      },
      undefined,
      (error) => {
        console.error('Error loading billboard texture:', error);
        // Fallback if image fails to load
        this.renderTextOnBillboard(parent, data.title, boardWidth, boardHeight, true, data.id);
      }
    );
  }
  
  /**
   * Render company name text on the billboard
   */
  private renderTextOnBillboard(
    parent: THREE.Object3D, 
    text: string, 
    boardWidth: number, 
    boardHeight: number, 
    centerText: boolean = false,
    billboardId: string
  ): void {
    // Create a canvas for the text
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    if (context) {
      // Set canvas size - increase resolution for sharper text
      canvas.width = 1024; // Double the resolution
      canvas.height = 512; // Double the resolution
      
      // Clear canvas (transparent background)
      context.clearRect(0, 0, canvas.width, canvas.height);
      
      // Add text with improved font settings
      context.fillStyle = 'white';
      context.font = 'bold 120px Nunito, Arial, sans-serif'; // Larger font for higher resolution
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      
      // Word wrap function for long text
      const wrapText = (text: string, maxWidth: number) => {
        const words = text.split(' ');
        let line = '';
        const lines = [];
        const lineHeight = 130; // Adjusted for higher resolution
        
        for (let n = 0; n < words.length; n++) {
          const testLine = line + words[n] + ' ';
          const metrics = context.measureText(testLine);
          const testWidth = metrics.width;
          
          if (testWidth > maxWidth && n > 0) {
            lines.push(line);
            line = words[n] + ' ';
          } else {
            line = testLine;
          }
        }
        
        lines.push(line);
        return lines;
      };
      
      const lines = wrapText(text, canvas.width - 120);
      const y = canvas.height / 2 - (lines.length - 1) * 65;
      
      lines.forEach((line, i) => {
        context.fillText(line, canvas.width / 2, y + i * 130);
      });
      
      // Create texture from canvas with improved settings
      const texture = new THREE.CanvasTexture(canvas);
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.anisotropy = 16; // Increase anisotropy for sharper rendering
      
      const textureMaterial = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true
      });
      
      // Create a plane for the text
      const textPlaneWidth = centerText ? boardWidth * 0.9 : boardWidth * 0.5;
      const textPlaneHeight = boardHeight * 0.6;
      const textGeometry = new THREE.PlaneGeometry(textPlaneWidth, textPlaneHeight);
      const textPlane = new THREE.Mesh(textGeometry, textureMaterial);
      
      // Make the text plane clickable
      textPlane.userData.isBillboard = true;
      textPlane.userData.billboardId = billboardId;
      
      // Position the text - centered if no image, otherwise on the right side
      const xPosition = centerText ? 0 : boardWidth * 0.2;
      textPlane.position.set(xPosition, 0, 0.03);
      parent.add(textPlane);
    }
  }
  
  /**
   * Show the overlay for a specific billboard
   */
  private showOverlay(billboardId: string): void {
    if (!this.overlayContainer) return;
    
    // Get the billboard data
    const data = this.billboardData.get(billboardId);
    if (!data) return;
    
    // Clear previous overlay content
    this.overlayContainer.innerHTML = '';
    
    // Create overlay content
    const overlayContent = document.createElement('div');
    overlayContent.className = 'billboard-content';
    
    // Close button
    const closeButton = document.createElement('button');
    closeButton.textContent = '×';
    closeButton.className = 'billboard-close-button';
    closeButton.onclick = () => this.hideOverlay();
    overlayContent.appendChild(closeButton);
    
    // Image if available and URL is not empty
    if (data.imageUrl && data.imageUrl.trim() !== '') {
      const image = document.createElement('img');
      image.src = data.imageUrl;
      image.className = 'billboard-image';
      image.alt = data.title;
      
      // Handle errors for external images
      image.onerror = () => {
        image.style.display = 'none';
      };
      
      overlayContent.appendChild(image);
    }
    
    // Title
    const title = document.createElement('h2');
    title.textContent = data.title;
    title.className = 'billboard-title';
    overlayContent.appendChild(title);
    
    // Description
    const description = document.createElement('p');
    description.textContent = data.description;
    description.className = 'billboard-description';
    overlayContent.appendChild(description);
    
    // Button if URL is provided
    if (data.url) {
      const button = document.createElement('a');
      button.href = data.url;
      button.target = '_blank';
      button.textContent = 'Learn More';
      button.className = 'billboard-button';
      overlayContent.appendChild(button);
    }
    
    // Add overlay content to container
    this.overlayContainer.appendChild(overlayContent);
    
    // Show the overlay
    this.overlayContainer.style.display = 'flex';
    
    // Close overlay when clicking outside content
    this.overlayContainer.onclick = (event) => {
      if (event.target === this.overlayContainer) {
        this.hideOverlay();
      }
    };
    
    // Close overlay with escape key
    const escHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.hideOverlay();
        document.removeEventListener('keydown', escHandler);
      }
    };
    
    document.addEventListener('keydown', escHandler);
  }
  
  /**
   * Hide the billboard overlay
   */
  private hideOverlay(): void {
    if (this.overlayContainer) {
      this.overlayContainer.style.display = 'none';
    }
  }
  
  /**
   * Add a billboard with specific company data
   */
  public addCompanyBillboard(companyId: string, position: THREE.Vector3, rotation: number): void {
    // Define company data based on ID
    let billboardData: BillboardData;
    
    // Add company data based on company ID
    if (companyId === 'madamereves') {
      billboardData = {
        id: 'billboard-madamereves',
        title: 'Madame Rêves',
        description: 'Tell me your dream and I\'ll tell you who you are. Write dream journals, analyze your dreams, and learn lucid dreaming with Madame Rêves. ',
        url: 'https://madame-reves.com',
        imageUrl: '/cartooncity_141_fbx/MadameReves-logo.png'
      };
    } else if (companyId === 'yourwebsite') {
      billboardData = {
        id: 'billboard-yourwebsite',
        title: 'Your own website here',
        description: 'Be seen by players, indie hackers from all other the world. Contact us.',
        url: 'mailto:gabrielle@postmandilemma.com',
        imageUrl: '/cartooncity_141_fbx/Your-Website.png'
      };
    } else {
      // Generic fallback or placeholder for future companies
      billboardData = {
        id: `billboard-${companyId}`,
        title: companyId.charAt(0).toUpperCase() + companyId.slice(1),
        description: 'Visit our website for more information.',
        url: `https://${companyId}.com`,
        imageUrl: ''
      };
    }
    
    // Standard dimensions for all billboards - match the screenshot
    const width = 8;
    const height = 3;
    
    // Create billboard with the company data - same content on both sides
    this.createBillboardWithBackSide(
      billboardData,
      billboardData, // Use exactly the same data for both sides
      position,
      rotation,
      width, 
      height
    );
  }
  
  /**
   * Create a billboard with identical front and back sides
   */
  private createBillboardWithBackSide(
    frontData: BillboardData,
    backData: BillboardData,
    position: THREE.Vector3,
    rotation: number,
    width: number,
    height: number
  ): void {
    // Create a group for the billboard
    const billboardGroup = new THREE.Group();
    billboardGroup.position.copy(position);
    billboardGroup.rotation.y = rotation;
    billboardGroup.userData.isBillboard = true;
    billboardGroup.userData.billboardId = frontData.id;
    
    // Improved post design - thinner and taller
    const postMaterial = new THREE.MeshPhongMaterial({
      color: 0x333333,
      flatShading: true
    });
    
    // Post dimensions - thinner width, taller height
    const postThickness = 0.3;  // Thinner posts
    const postHeight = height + 3;  // Shorter posts (changed from height + 6)
    const postGeometry = new THREE.BoxGeometry(postThickness, postHeight, postThickness);
    
    // Left post
    const leftPost = new THREE.Mesh(postGeometry, postMaterial);
    leftPost.position.set(-width / 2, postHeight / 2 - 1, 0);  // Positioned at ground level
    leftPost.castShadow = true;
    leftPost.receiveShadow = true;
    leftPost.userData.billboardId = frontData.id; // Add billboard ID to each component
    billboardGroup.add(leftPost);
    
    // Right post
    const rightPost = new THREE.Mesh(postGeometry, postMaterial);
    rightPost.position.set(width / 2, postHeight / 2 - 1, 0);  // Positioned at ground level
    rightPost.castShadow = true;
    rightPost.receiveShadow = true;
    rightPost.userData.billboardId = frontData.id; // Add billboard ID to each component
    billboardGroup.add(rightPost);
    
    // Standard dark color for all boards
    const boardColor = 0x1a1a1a;
    
    // Create the board material - same for both sides
    const boardMaterial = new THREE.MeshPhongMaterial({
      color: boardColor,
      flatShading: true
    });
    
    // Create front and back boards with identical data
    this.createBillboardSide(billboardGroup, boardMaterial, width, height, postHeight, 0.1, frontData, true);  // Front side
    this.createBillboardSide(billboardGroup, boardMaterial, width, height, postHeight, -0.1, frontData, false); // Back side - use front data
    
    // Add to scene and store reference
    this.scene.add(billboardGroup);
    this.billboards.set(frontData.id, billboardGroup as unknown as THREE.Mesh);
    this.billboardData.set(frontData.id, frontData);
    
    // Debug log to confirm billboard creation
    console.log(`Billboard created: ${frontData.id} at position:`, position);
  }
  
  /**
   * Place billboards around the city in strategic locations
   */
  public placeBillboardsInCity(citySize: number, blockSize: number, roadWidth: number): void {
    console.log("Placing billboards in the city");
    
    // Calculate grid size based on city parameters
    const gridSize = Math.floor(citySize / blockSize);
    
    // Place billboards at strategic intersections with Madame Rêves and Your Website
    const placementPositions = [
      // { id: 'madamereves', x: gridSize * 0.25, z: gridSize * 0.25, rotation: Math.PI / 4 },
      // { id: 'yourwebsite', x: gridSize * 0.1, z: gridSize * 0.1, rotation: Math.PI / 4 },
      // { id: 'yourwebsite', x: gridSize * 0.5, z: gridSize * 0.45, rotation: Math.PI / 2 },
      { id: 'madamereves', x: gridSize * 0.15, z: gridSize * 0.5, rotation: Math.PI * 0.75 },
      { id: 'yourwebsite', x: gridSize * 0.75, z: gridSize * 0.25, rotation: Math.PI * 0.75 },
      // { id: 'yourwebsite', x: gridSize * 0.9, z: gridSize * 0.1, rotation: Math.PI * 0.75 },
      // { id: 'yourwebsite', x: gridSize * 0.25, z: gridSize * 0.75, rotation: -Math.PI / 4 },
      // { id: 'madamereves', x: gridSize * 0.75, z: gridSize * 0.75, rotation: -Math.PI * 0.75 }
    ];
    
    // Create billboards at the calculated positions
    placementPositions.forEach((pos) => {
      // Calculate the actual position in the world
      const worldX = pos.x * blockSize;
      const worldZ = pos.z * blockSize;
      const position = new THREE.Vector3(worldX, 1, worldZ);
      
      this.addCompanyBillboard(pos.id, position, pos.rotation);
    });
  }
  
  /**
   * Clean up resources
   */
  public cleanup(): void {
    // Remove event listener
    if (this.clickEventListener) {
      window.removeEventListener('click', this.clickEventListener);
    }
    
    // Remove billboards from scene
    this.billboards.forEach((billboard) => {
      this.scene.remove(billboard);
    });
    
    // Clear maps
    this.billboards.clear();
    this.billboardData.clear();
    
    // Remove overlay container
    if (this.overlayContainer && this.overlayContainer.parentNode) {
      this.overlayContainer.parentNode.removeChild(this.overlayContainer);
    }
    
    this.overlayContainer = null;
  }
} 